import * as React from 'react';
import {useParams} from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  Cluster,
  Cover,
  DateTime,
  Divider,
  Drawer,
  Expand,
  FormItem,
  Grid,
  Heading,
  Help,
  Icon,
  ISimpleScheduleEntry,
  Label,
  Paragraph,
  Popover,
  Range,
  Select,
  Stack,
  Table,
  TdMultilineTruncate,
  TZoomLevelsScheduler,
  Template,
  TextInput,
  Timeline,
  Toast,
  summon,
  secondsToHms,
  IScheduleEntry,
  ISelectedDrop,
  useUndo,
  trigger,
  on,
  off,
  timelineDragHandler,
  ITimelineRef,
} from '@pluto-tv/assemble';
import {debounce, get} from 'lodash-es';
import {DateTime as luxon} from 'luxon';

import {hasOverlap} from 'views/programming/channel/utils/recurrence';
import {
  ITimeline,
  useFindDraftTimelinesQuery,
  useFindTimelinesQuery,
} from 'features/channelTimelines/channelTimelinesApi';
import useToggleSearchBarOnSlash from 'helpers/useToggleSearchBarOnSlash';
import {IChannelCatalogItem} from 'models/channelCatalog';
import EpisodesDetails, {
  isSimpleScheduleEntry,
} from 'views/programming/channel/edit/program/components/EpisodesDetails';
import {
  findMaxManualOverride,
  formatScreeningTime,
  startEndOfWeek,
  disablePastDates,
  formatDateWithLocalTimezone,
} from 'utils/dateUtils';
import {INestedChannelProgramProps} from '../nestedPropsInterface';
import {
  ITimeframe,
  getDraftTimeline,
  getMinStart,
  getTimelineState,
  isLockedSlot,
  mapTimelinesIntoTimelinesEntry,
  mapToSimpleScheduleEntries,
  addEpisodes,
  getStartTimeToDrop,
  sanitizeDraftEventList,
  addDays,
  normalizeDate,
  supplementDraftWithPublishedLocked,
} from './utilities';
import {useKeyboardShortcut} from 'views/programming/channel/edit/program/hooks/useKeyboardShortcut';
import {recalculateEventTimes} from './utilities';
import {useMultiSelect} from 'views/programming/channel/edit/program/hooks/useMultiSelect';
import {useChannelCatalogQueue} from 'views/programming/channel/edit/catalog/hooks/useChannelCatalogQueue';
import {ChannelCatalogItemWithState as IChannelCatalogItemWithState} from 'views/programming/channel/contexts/getMemoryListProvider';
import {LoadingBox} from 'components/loadingBox';
import {useAppPermissions} from 'app/permissions';
import EpisodeRecurrence from 'views/programming/channel/edit/program/components/EpisodeRecurrence';
import useRecurrenceValidation from 'views/programming/channel/edit/program/hooks/useRecurrenceValidation';
import {useProgrammingQueue} from 'views/programming/channel/edit/program/hooks/useProgrammingQueue';
import {RowState} from 'components/rowState';
import {useShared, useSharedDispatch} from '../sharedContext';
import {StateColorType} from '../../utils';

import './index.css';

export type SelectMode = 'vertical-select' | 'horizontal-select' | 'group-select';

export interface IProgramRef {
  cleanPeriod: () => void;
}

const MIN_GAP_TIME = 30; // 30 minutes

const TABLE_COLUMN_NAMES = {
  Episode: 'name',
  Author: 'author',
  'Rat.': 'rating',
  Series: 'series.name',
  S: 'season',
  E: 'number',
  'Dur.': 'duration',
} as const;

const TableColumnLabel = {
  name: 'Episode',
  rating: 'Rat.',
  'series.name': 'Series',
  season: 'S',
  number: 'E',
  duration: 'Dur.',
  author: 'Author',
};

const EpisodeQueueSearchModal = React.lazy(
  () => import('views/programming/channel/edit/program/components/EpisodeQueueSearchModal'),
);

const LeaveCalendarDialog = React.lazy(
  () => import('views/programming/channel/edit/program/components/LeaveCalendarDialog'),
);

const mapCatalogToEntry = (row: IChannelCatalogItemWithState | ISimpleScheduleEntry): IScheduleEntry => ({
  duration: row.duration,
  fullHeight: '3rem',
  height: 'auto',
  id: row.id,
  offsetTop: '0px',
  rating: row.rating,
  title: row.series.name,
  subTitle: row.name,
  start: new Date(),
  state: row.state?.toString() as StateColorType,
});

function useClickOutside(
  ref: React.RefObject<HTMLElement>,
  onClickOutside: () => void,
  ignoreRefs: React.RefObject<HTMLElement>[] = [],
) {
  React.useEffect(() => {
    function handleClick(event: MouseEvent) {
      const target = event.target as Node;
      if (ref?.current && !ref.current.contains(target)) {
        // Don't fire when the click lands inside an ignored area (e.g. the queue tray),
        // so opening/switching episodes there doesn't close the panel.
        if (ignoreRefs.some(ignored => ignored.current?.contains(target))) {
          return;
        }
        onClickOutside();
      }
    }

    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, [ref, onClickOutside, ignoreRefs]);
}

const ProgramPage = React.memo(
  React.forwardRef<IProgramRef, INestedChannelProgramProps>(
    (
      {
        model,
        programModel,
        onGapsChange,
        onConflictsChange,
        programHasChanges,
        setProgramFields,
        setProgramModel,
        onClickSaveHandler,
        isDraft,
        setIsDraft,
        moveToPeriod,
        isOpenLeaveDialog,
        setIsOpenLeaveDialog,
        lastProgrammingUpdate,
        attemptToLeave,
        cancelAttemptToLeave,
        setGapChecksPending,
        isChannelDraftDiscarded,
        isDiscardTimelineDraftsLoading,
      }: INestedChannelProgramProps,
      ref,
    ) => {
      const {id: channelId} = useParams<{id: string}>();

      // Ref to track mounted state
      const isMountedRef = React.useRef(false);

      // Update the ref on mount/unmount
      React.useEffect(() => {
        isMountedRef.current = true;

        return () => {
          isMountedRef.current = false;
        };
      }, []);

      const {dayReference, viewType, dayValue, endDate, startDate} = useShared();
      const {createNewTimelinesAutoFill, findEpisodeInQueue} = useProgrammingQueue();

      const dispatch = useSharedDispatch();

      const [isQueueExpanded, setIsQueueExpanded] = React.useState(true);
      const [isRightExpanded, setIsRightExpanded] = React.useState(false);
      const [isSnapOn, setIsSnapOn] = React.useState(false);

      const [queueSearch, setQueueSearch] = React.useState('');
      const queueSearchInputId = 'queueSearchInput';

      const queueContainerRef = React.useRef<HTMLDivElement>(null);
      const [isQueueActive, setIsQueueActive] = React.useState(false);

      const [isAddEpisodeOpen, setIsAddEpisodeOpen] = React.useState(false);
      const [isRecurrenceOpen, setIsRecurrenceOpen] = React.useState(false);
      const [timeToAddNewEntry, setTimeToAddNewEntry] = React.useState<number | undefined>();

      const [episodeInfo, setEpisodeInfo] = React.useState<'details' | 'recurrence'>('details');

      const clipboardRef = React.useRef<(ISimpleScheduleEntry | IChannelCatalogItem)[]>([]);
      const clipboardTimelinesRef = React.useRef<ITimeline[]>([]);

      const [composedRecurrenceMessage, setComposedRecurrenceMessage] = React.useState<boolean>(false);

      const [lastRepeatSchedule, setLastRepeatSchedule] = React.useState<number>();

      const [copyMode, setCopyMode] = React.useState<SelectMode>('vertical-select');

      const {set: setUndo, undo, redo, canUndo, canRedo, reset: resetUndo} = useUndo<ITimeline[]>();

      const {
        isValidRecurrence,
        recurrenceWarning,
        repeatPeriod,
        endRepeatType,
        endRepeatDate,
        timeframe,
        cycleOver,
        occurrences,
        sunRepeat,
        monRepeat,
        tueRepeat,
        wedRepeat,
        thuRepeat,
        friRepeat,
        satRepeat,
        positioning,
        setPositioning,
        setRepeatPeriod,
        setEndRepeatType,
        setEndRepeatDate,
        setTimeframe,
        setCycleOver,
        setOccurrences,
        setSunRepeat,
        setMonRepeat,
        setTueRepeat,
        setWedRepeat,
        setThuRepeat,
        setFriRepeat,
        setSatRepeat,
        handleAddRepeatSchedule: handleRepeatSchedule,
      } = useRecurrenceValidation(channelId);

      const timelineRef = React.useRef<ITimelineRef>(null);
      const episodeRecurrenceRef = React.useRef<HTMLDivElement>(null);

      useClickOutside(
        episodeRecurrenceRef,
        () => {
          setShouldOpenRightInfoPanel(false);
        },
        [queueContainerRef],
      );
      const {ableTo, permissions} = useAppPermissions();
      // end recurrence options

      // Entries to display in the calendar view
      const [publishedTimelinesEntries, setPublishedTimelinesEntries] = React.useState<ISimpleScheduleEntry[]>([]);
      const [draftTimelinesEntries, setDraftTimelinesEntries] = React.useState<ISimpleScheduleEntry[] | undefined>();
      const draftTimelines = React.useMemo(() => programModel.draft, [programModel.draft]);

      // Used to clean the entries arrays to not display the green pulse when the calendar
      // is switched from draft to publish or viceversa.
      const [isReady, setIsReady] = React.useState(false);

      const sortedDraftTimelines = React.useMemo(() => {
        return draftTimelines?.slice().sort((a, b) => new Date(a.stop).getTime() - new Date(b.stop).getTime());
      }, [draftTimelines]);

      React.useEffect(() => {
        setPublishedTimelinesEntries([]);
        setDraftTimelinesEntries([]);
        setIsReady(true);
      }, [isDraft]);

      React.useEffect(() => {
        if (!isReady || !draftTimelines || !isDraft) return;

        const timelinesForDisplay = supplementDraftWithPublishedLocked(draftTimelines, programModel.published || []);

        setDraftTimelinesEntries(
          mapTimelinesIntoTimelinesEntry(
            timelinesForDisplay,
            !!(model.tmsid && model.gracenoteIntegration?.dynamicClip),
            isDraft && ableTo('CHANNEL_EPG_EDIT'),
          ),
        );
      }, [
        model.gracenoteIntegration?.dynamicClip,
        model.tmsid,
        draftTimelines,
        isDraft,
        ableTo,
        isReady,
        programModel.published,
      ]);

      // Prune stale selectedEpisodes that no longer exist in the current entries
      React.useEffect(() => {
        if (!isDraft || !draftTimelinesEntries) return;
        setSelectedEpisodes(prev => {
          if (prev.length === 0) return prev;
          const validIds = new Set(draftTimelinesEntries.map(e => e.id));
          // Only prune calendar entries; queue rows (no `start`) are never on the calendar
          // and must survive so multi-drag from the queue keeps its selection.
          const pruned = prev.filter(ep => ep && (!isSimpleScheduleEntry(ep) || validIds.has(ep.id)));
          return pruned.length === prev.length ? prev : pruned;
        });
      }, [draftTimelinesEntries, isDraft]);

      const setDraftTimelinesToModel = React.useCallback(
        (timelines: ITimeline[]): void => {
          setProgramFields({draft: timelines});
        },
        [setProgramFields],
      );

      React.useEffect(() => {
        setGapChecksPending(true);
        setTimeout(() => setShouldCheckForGaps(true), 2500);
      }, [draftTimelinesEntries, setGapChecksPending]);

      const [autofillDaysOptions, setAutofillDaysOptions] = React.useState<{label: string; value: number}[]>([]);
      const [daysToAutofillSelected, setDaysToAutofillSelected] = React.useState<number>(1);

      const [isAddingQueueAutofill, setIsAddingQueueAutofill] = React.useState(false);

      const [selectedEpisodes, setSelectedEpisodes] = React.useState<ISimpleScheduleEntry[]>([]);
      const [selectedQueue, setSelectedQueue] = React.useState<IChannelCatalogItem[]>([]);

      // Flag used to show entries in the right panel when click on them
      const [shouldOpenRightInfoPanel, setShouldOpenRightInfoPanel] = React.useState(false);

      const [isEditingRecurrenceField, setIsEditingRecurrenceField] = React.useState(false);
      const [recurrenceResponseFromModal, setRecurrenceResponse] = React.useState();

      const [lockedEvent, setLockedEvent] = React.useState<ITimeline | null>(null);

      const [zoomLevel, setZoomLevel] = React.useState<TZoomLevelsScheduler>(1);
      const [increment, setIncrement] = React.useState<'small' | 'medium'>('medium');

      const [eventInTimeline, setEventInTimeline] = React.useState({});

      const timestampRef = React.useRef<number | null>();

      React.useImperativeHandle(ref, () => ({
        cleanPeriod: () => {
          timestampRef.current = undefined;
          setShouldOpenRightInfoPanel(false);
          setSelectedEpisodes([]);
          setSelectedQueue([]);
          setDraftTimelinesEntries([]);
          setPublishedTimelinesEntries([]);
          resetUndo();
        },
      }));

      const handleCopy = () => {
        if (!ableTo('CHANNEL_EPG_EDIT') || (!selectedEpisodes.length && !selectedQueue.length)) return;
        const isTimelineList = selectedEpisodes.length > 0;

        // keep track of copied timelines entries, they are needed when pasting in other weeks
        if (isTimelineList) {
          clipboardTimelinesRef.current = selectedEpisodes.reduce((acc, episode) => {
            const timeline = draftTimelines?.find(timeline => timeline.id === episode.id);
            if (!timeline) return acc;
            return [...acc, timeline];
          }, [] as ITimeline[]);
        }

        clipboardRef.current = isTimelineList ? selectedEpisodes : selectedQueue;

        Toast.success(`${clipboardRef.current.length} items copied to clipboard`);
        // Trigger event so the Timeline component can handle it and show the highlight and the paste icon
        trigger(
          'timeline-copy',
          !isTimelineList
            ? mapToSimpleScheduleEntries(clipboardRef.current)
            : (clipboardRef.current as ISimpleScheduleEntry[]),
        );
      };

      const handleUndo = () => {
        if (!ableTo('CHANNEL_EPG_EDIT') || !canUndo()) return;

        const previousTimeline = undo();

        if (previousTimeline) {
          setDraftTimelinesToModel(previousTimeline);
        }
      };

      const handleRedo = () => {
        if (!ableTo('CHANNEL_EPG_EDIT') || !canRedo()) return;

        const timeline = redo();

        if (timeline) {
          setDraftTimelinesToModel(timeline);
        }
      };

      const handleDelete = () => {
        const episodesToRemove = selectedEpisodes.filter(episode => !episode.locked);
        if (
          !ableTo('CHANNEL_EPG_EDIT') ||
          !episodesToRemove.length ||
          (isRightExpanded && episodeInfo === 'recurrence' && isEditingRecurrenceField)
        )
          return;

        const newDraftTimeline = (draftTimelines || []).filter(timeline => {
          return !episodesToRemove.some(episode => episode.id === timeline.id);
        });

        setDraftTimelinesToModel(newDraftTimeline);
        setSelectedEpisodes(prevEpisodes => prevEpisodes.filter(episode => episode.locked));
        setUndo(newDraftTimeline);
      };

      const handleRemove = (id: string) => {
        const newArray = (draftTimelines || []).filter(item => item.id !== id);

        setDraftTimelinesToModel(newArray);

        setSelectedEpisodes(prevItems => prevItems.filter(item => item.id !== id));
        setUndo(newArray);
      };

      const debouncedZoom = React.useMemo(() => debounce(val => setZoomLevel(val), 500), []);

      // These are used to enable the Autofill button
      const [existGapsInTimeline, setExistGapsInTimeline] = React.useState(false);
      const [openMsgWhenAutofillDisabled, setOpenMsgWhenAutofillDisabled] = React.useState(false);

      const [shouldCheckForGaps, setShouldCheckForGaps] = React.useState(true);

      useToggleSearchBarOnSlash(
        setIsQueueExpanded,
        isQueueExpanded,
        model?.gracenoteIntegration?.enabled || isAddEpisodeOpen,
      );

      const {
        currentData: draftTimelinesApiResponse,
        isLoading: isDraftTimelinesLoading,
        isFetching: isDraftTimelinesFetching,
        isSuccess: isDraftTimelinesSuccess,
        isError: isDraftTimelinesError,
      } = useFindDraftTimelinesQuery(
        {
          channelId,
          startDate: startDate?.getTime() || 0,
          endDate: endDate?.getTime() || 0,
          lastUpdate: lastProgrammingUpdate.valueOf(),
          draftDiscarded: isChannelDraftDiscarded,
        },
        {skip: !startDate && !endDate, refetchOnMountOrArgChange: true},
      );

      const {
        currentData: timelines,
        isLoading: isTimelinesLoading,
        isFetching: isTimelinesFetching,
        isSuccess: isTimelinesSuccess,
        isError: isTimelinesError,
      } = useFindTimelinesQuery(
        {
          channelId,
          startDate: startDate?.getTime() || 0,
          endDate: endDate?.getTime() || 0,
          lastUpdate: lastProgrammingUpdate.valueOf(),
          draftDiscarded: isChannelDraftDiscarded,
        },
        {skip: !startDate && !endDate, refetchOnMountOrArgChange: true},
      );

      const {
        listItems: queueItemsRetrieved,
        isLazyLoading: isQueueLazyLoading,
        isLoading: isQueueLoading,
        isError: isQueueError,
        totalItems: queueTotalItems,
        handleLazyLoad: handleQueueLazyLoad,
        duration: queueDuration,
        handleSorting: handleQueueSorting,
        handleFiltering: handleQueueFiltering,
        sort: queueSort,
      } = useChannelCatalogQueue(model.id || '');

      // Handle Enter key press to trigger search (same functionality as catalog tab)
      React.useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
          const input = document.getElementById(queueSearchInputId);
          if (event.key === 'Enter' && document.activeElement === input && input instanceof HTMLInputElement) {
            event.preventDefault();
            const trimmedSearch = queueSearch.trim();
            // If search is empty, reset filter (same as catalog handleSearchClear)
            if (!trimmedSearch) {
              handleQueueFiltering({name: undefined});
            } else {
              // Use same format as catalog: {name: 'episode name'}
              handleQueueFiltering({name: trimmedSearch});
            }
          }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
          document.removeEventListener('keydown', handleKeyDown);
        };
      }, [queueSearch, handleQueueFiltering, queueSearchInputId]);

      const [handleClickQueueEntries] = useMultiSelect<IChannelCatalogItem>(
        queueItemsRetrieved,
        selectedQueue,
        setSelectedQueue,
      );

      // Ignore a close that lands right after opening, so a rapid burst of double clicks
      // on the same episode can't toggle the panel open then immediately closed.
      const REOPEN_GUARD_MS = 400;
      const lastOpenAtRef = React.useRef<number>(0);

      const onClickQueueTableRow = React.useCallback(
        (row: IChannelCatalogItemWithState, index: number, ev?: React.MouseEvent) => {
          // Toggle the right info panel on double click, using the native click count
          // forwarded by TdMultilineTruncate. Double clicking the open episode closes it;
          // double clicking another episode switches to it.
          if (ev?.detail !== 2) {
            return;
          }

          const isSameEpisodeOpen = isRightExpanded && selectedQueue.length === 1 && row.id === selectedQueue[0].id;

          if (isSameEpisodeOpen) {
            // Skip the close if it arrives within the guard window after opening.
            if (performance.now() - lastOpenAtRef.current < REOPEN_GUARD_MS) {
              return;
            }
            setIsRightExpanded(false);
            setShouldOpenRightInfoPanel(false);
          } else {
            setShouldOpenRightInfoPanel(true);
            lastOpenAtRef.current = performance.now();
          }

          if (selectedEpisodes.length > 0) {
            setSelectedEpisodes([]);
          }

          handleClickQueueEntries([index]);
        },
        [
          handleClickQueueEntries,
          selectedEpisodes.length,
          setSelectedEpisodes,
          setShouldOpenRightInfoPanel,
          selectedQueue,
          isRightExpanded,
        ],
      );

      const handleSorting = React.useCallback(
        (sortField: string) => {
          handleQueueSorting(sortField);
        },
        [handleQueueSorting],
      );

      const queueOnSort = React.useCallback(
        (columnLabel: string) => handleSorting(TABLE_COLUMN_NAMES[columnLabel]),
        [handleSorting],
      );

      const [handleClickItems] = useMultiSelect<ISimpleScheduleEntry>(
        isDraft ? draftTimelinesEntries || [] : publishedTimelinesEntries,
        selectedEpisodes,
        setSelectedEpisodes,
      );

      React.useEffect(() => {
        const validEpisodes = selectedEpisodes.filter(Boolean);
        trigger(
          'selectedTimelineEntries',
          validEpisodes.map(e => mapCatalogToEntry(e)),
        );
      }, [selectedEpisodes]);

      const onCustomRowDrag = React.useCallback((ev, row) => {
        timelineDragHandler.startDrag(ev, mapCatalogToEntry(row));
      }, []);

      const canModifyEdit = React.useMemo(
        (): boolean =>
          !model?.gracenoteIntegration?.enabled &&
          !model?.cfaasIntegration?.enabled &&
          isDraft &&
          ableTo('CHANNEL_EPG_EDIT'),
        [isDraft, model?.gracenoteIntegration?.enabled, model?.cfaasIntegration?.enabled, ableTo],
      );

      const handleCloseRightPanel = React.useCallback(() => {
        setIsRightExpanded(false);
        setShouldOpenRightInfoPanel(false);
        setSelectedEpisodes([]);
        setSelectedQueue([]);
        setEpisodeInfo('details');
        setIsRecurrenceOpen(false);
      }, []);

      // This will close right panel and clean all selected list items
      useKeyboardShortcut('escape', handleCloseRightPanel);

      // Open recurrence from shortcut
      useKeyboardShortcut('ctrl+i, command+i', () => setIsRecurrenceOpen(!!(selectedEpisodes.length === 1)));

      // Track when user clicks outside the queue to allow native browser search
      useClickOutside(queueContainerRef, () => setIsQueueActive(false));

      // Focus search input on cmd+f (Mac) or ctrl+f (other systems)
      // Only intercept when queue is active (user clicked in queue area)
      useKeyboardShortcut('ctrl+f, command+f', (event: KeyboardEvent) => {
        // If queue is not active, allow native browser search
        if (!isQueueActive) {
          return;
        }

        // Prevent browser's default find behavior
        event.preventDefault();
        event.stopPropagation();

        // Ensure queue is expanded
        if (!isQueueExpanded && isDraft && ableTo('CHANNEL_EPG_EDIT')) {
          setIsQueueExpanded(true);
        }

        // Focus the search input
        setTimeout(() => {
          const input = document.getElementById(queueSearchInputId);
          if (input instanceof HTMLInputElement) {
            input.focus();
            input.select();
          }
        }, 0);
      });

      React.useEffect(() => {
        if (selectedEpisodes.length || selectedQueue.length) {
          setEpisodeInfo('details');
        }
      }, [selectedEpisodes, selectedQueue]);

      React.useEffect(() => {
        handleCloseRightPanel();
      }, [handleCloseRightPanel, isDraft]);

      const isScheduleLoading = React.useMemo(() => {
        if (isDraft) {
          return isDraftTimelinesLoading || isDraftTimelinesFetching;
        }

        return isTimelinesLoading || isTimelinesFetching;
      }, [isDraft, isTimelinesLoading, isDraftTimelinesLoading, isDraftTimelinesFetching, isTimelinesFetching]);

      const transformEntry = React.useCallback((item: IScheduleEntry) => <EpisodeTemplate item={item} />, []);
      const transformTopEntry = React.useCallback((item: IScheduleEntry) => <EpisodeTopTemplate item={item} />, []);

      const getTimelinesConsideringError = (isSuccess: boolean, timelines?: ITimeline[]): ITimeline[] => {
        if (isSuccess) {
          return timelines || [];
        }

        return [];
      };

      React.useEffect(() => {
        if (isDraftTimelinesLoading || isTimelinesLoading || isDraftTimelinesFetching || isTimelinesFetching) return;

        if ((isDraftTimelinesSuccess || isDraftTimelinesError) && (isTimelinesSuccess || isTimelinesError)) {
          const {events, borderEvent} = getDraftTimeline(
            getTimelinesConsideringError(isDraftTimelinesSuccess, draftTimelinesApiResponse),
            getTimelinesConsideringError(isTimelinesSuccess, timelines),
          );

          const draftEvents = events.map(item => {
            // Normalize times (remove milliseconds) when loading from server
            // This ensures consistent times and prevents overlap issues
            const start = normalizeDate(new Date(item.start));
            const stop = normalizeDate(new Date(item.stop));
            const {state, stateMsgList} = getTimelineState({...item, ...item.episode});
            return {
              ...item,
              start,
              stop,
              locked: start < getMinStart() || model.gracenoteIntegration?.enabled || model.cfaasIntegration?.enabled,
              state,
              stateMsgList,
            };
          });

          setProgramModel({
            draft: draftEvents,
            published: timelines?.map(t => ({
              ...t,
              start: normalizeDate(new Date(t.start)),
              stop: normalizeDate(new Date(t.stop)),
            })),
            startDate: startDate?.getTime() || 0,
            endDate: endDate?.getTime() || 0,
          });

          if (borderEvent) {
            setLockedEvent(borderEvent);
          }
          setUndo(draftEvents, {locked: true});
        }
      }, [
        isDraftTimelinesLoading,
        isDraftTimelinesFetching,
        isTimelinesLoading,
        isTimelinesFetching,
        draftTimelinesApiResponse,
        timelines,
        endDate,
        isDraftTimelinesSuccess,
        isTimelinesSuccess,
        isDraftTimelinesError,
        isTimelinesError,
        model.gracenoteIntegration?.dynamicClip,
        model.gracenoteIntegration?.enabled,
        model.cfaasIntegration?.enabled,
        model.tmsid,
        setProgramModel,
        setUndo,
        startDate,
      ]);

      React.useEffect(() => {
        let {startDate: newStartDate, endDate: newEndDate} = startEndOfWeek(dayReference);

        newStartDate = new Date(newStartDate.setHours(0, 0, 0, 0));
        newEndDate = new Date(newEndDate.setHours(23, 59, 59, 999));

        // when switching from viewType day to week there's no need to set again the start and end dates.
        if (startDate?.getTime() === newStartDate.getTime() && endDate!.getTime() === newEndDate.getTime()) return;

        dispatch({
          type: 'UPDATE_START_END_DATES',
          payload: {
            endDate: newEndDate,
            startDate: newStartDate,
          },
        });

        const todayDate = new Date();
        let totalDays: number;
        let selectedDayToStart = 1;

        if (newStartDate > todayDate) {
          // Future week
          totalDays = 7;
        } else if (newEndDate < todayDate) {
          // Previous week
          totalDays = 0;
          selectedDayToStart = 0;
        } else {
          // current week (normalize Sunday from 0 to 7 for Mon-Sun calendar)
          const dayOfWeek = todayDate.getDay() || 7;
          totalDays = 7 - dayOfWeek + 1;
        }

        const options: {label: string; value: number}[] = [];

        for (let index = 0; index < totalDays; index++) {
          options.push({label: (index + 1).toString(), value: index + 1});
        }

        setAutofillDaysOptions(options);
        setDaysToAutofillSelected(selectedDayToStart);
        cancelAttemptToLeave();

        isDraft ? setDraftTimelinesEntries([]) : setPublishedTimelinesEntries([]);

        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [dayReference, dispatch]);

      const [isPageLoading, setIsPageLoading] = React.useState(true);

      React.useEffect(() => {
        setTimeout(() => {
          if (isMountedRef.current) {
            setIsPageLoading(!startDate || isDraftTimelinesLoading || isTimelinesLoading || isQueueLoading);
          }
        }, 500);
      }, [isDraftTimelinesLoading, isTimelinesLoading, isQueueLoading, startDate]);

      React.useEffect(() => {
        if (isDraft || !isReady) return;

        if (!isTimelinesLoading && timelines?.length) {
          const publishedTimelines: ITimeline[] = timelines.map(timeline => {
            // Normalize times (remove milliseconds) when loading from server
            // This ensures consistent times and prevents overlap issues
            const start = normalizeDate(new Date(timeline.start));
            const stop = normalizeDate(new Date(timeline.stop));
            const {state, stateMsgList} = getTimelineState({...timeline, ...timeline.episode});

            return {
              ...timeline,
              start,
              stop,
              locked: start < getMinStart() || model.gracenoteIntegration?.enabled || model.cfaasIntegration?.enabled,
              state,
              stateMsgList,
            };
          });
          setPublishedTimelinesEntries(
            mapTimelinesIntoTimelinesEntry(
              publishedTimelines || [],
              !!(model.tmsid && model.gracenoteIntegration?.dynamicClip),
              false,
            ),
          );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [
        isTimelinesLoading,
        model.tmsid,
        timelines,
        isDraft,
        isReady,
        model.gracenoteIntegration?.enabled,
        model.cfaasIntegration?.enabled,
      ]);

      React.useEffect(() => {
        if (isQueueError) {
          Toast.error('There was an error fetching the Episodes queue, please try again.');
          setIsAddingQueueAutofill(false);
        }
      }, [isQueueError]);

      React.useEffect(() => {
        const checkForGapsConflicts = async () => {
          if (isDraft && shouldCheckForGaps && endDate) {
            if (!draftTimelines?.length) {
              setGapChecksPending(false);
              setExistGapsInTimeline(true);
              onGapsChange([]);
              onConflictsChange([]);
              return;
            }

            let existGaps = false;
            let gaps = (await timelineRef.current?.getGaps()) || [];
            let conflicts = (await timelineRef.current?.getConflicts()) || [];

            onGapsChange(gaps);

            const now = new Date().getTime();

            if (gaps?.length > 0) {
              gaps = gaps.filter(gap => gap.start < endDate.getTime() && gap.end > now);
              existGaps = gaps?.length > 0;
            }

            if (!existGaps && draftTimelines.length > 0) {
              const lastStopTime = Math.max(...draftTimelines.map(t => new Date(t.stop).getTime()));
              if (lastStopTime < endDate.getTime() && endDate.getTime() > now) {
                existGaps = true;
              }
            }

            if (conflicts?.length > 0) {
              conflicts = conflicts.filter(c => c.start < endDate.getTime() && c.start > now);
            }

            onConflictsChange(conflicts);

            setExistGapsInTimeline(existGaps);
            setShouldCheckForGaps(false);
            setGapChecksPending(false);
          }
        };

        checkForGapsConflicts();
      }, [draftTimelines, endDate, isDraft, onGapsChange, shouldCheckForGaps, setGapChecksPending, onConflictsChange]);

      const switchView = React.useCallback(
        (viewType: 'day' | 'week'): void => {
          if (viewType === 'day') {
            const today = new Date();
            const dayRefereceToSet = startDate && endDate && startDate <= today && endDate >= today ? today : startDate;

            dispatch({
              payload: {
                viewType,
                dayReference: dayRefereceToSet!,
              },
              type: 'UPDATE_VIEW_TYPE_DAY_REFERENCE',
            });
          } else {
            dispatch({
              payload: {
                viewType,
              },
              type: 'UPDATE_VIEW_TYPE',
            });
          }
        },
        [endDate, startDate, dispatch],
      );

      // Start date where events can be added or deleted
      const getCurrentStartDate = React.useCallback(() => {
        const now = new Date();

        const lockedEventStop = lockedEvent ? new Date(lockedEvent.stop) : null;
        const today = lockedEventStop && lockedEventStop > now ? lockedEventStop : now;
        let start = startDate;

        if (!start || start < today) {
          start = today;
        }

        return {start, today};
      }, [lockedEvent, startDate]);

      //#region AUTOFILL

      const searchItemAboveTimeFrame = (gapStart: Date, sortedTimelines: ITimeline[]): ITimeline | undefined => {
        let left = 0;
        let right = sortedTimelines.length - 1;
        let result: ITimeline | undefined = undefined;

        while (left <= right) {
          const mid = Math.floor((left + right) / 2);
          const midStopTime = new Date(sortedTimelines[mid].stop).getTime();

          if (midStopTime <= gapStart.getTime()) {
            result = sortedTimelines[mid];
            left = mid + 1;
          } else {
            right = mid - 1;
          }
        }

        return result;
      };

      const getItemAboveTimeFrame = (
        gap: {start: Date; stop: Date},
        sortedTimelines: ITimeline[],
      ): ITimeline | undefined => {
        if (!sortedTimelines?.length) return undefined;
        return searchItemAboveTimeFrame(gap.start, sortedTimelines);
      };

      const getTimeframes = (start: Date, stop: Date) => {
        // Gaps to fill in

        // if there's no timelines, create only 1 timeframe from our start and stop original values
        let timeframes = [{start, stop}];

        /**
         * This finds the starting point in which to begin autofilling content.
         * This start point is the end of the last programmed episode in the programming range.
         */
        const filteredDraftTimelines = draftTimelines?.filter(
          draft => (draft.stop > start && draft.stop <= stop) || (draft.start >= start && draft.start < stop),
        );

        if (filteredDraftTimelines?.length) {
          let currStart = start;

          timeframes = [];

          filteredDraftTimelines
            .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
            ?.forEach(timeline => {
              // if timeline.start is greater than our original start point it means
              // that we have a gap, therefore we add this timeframe in our list
              // and set the start point to be the end of that timeline
              const timelineStart = new Date(timeline.start);
              if (timelineStart.getTime() > currStart.getTime()) {
                timeframes.push({start: currStart, stop: timelineStart});
              }

              currStart = new Date(timeline.stop);
            });

          // check if there's a gap between our last timeline and our stop
          if (stop && stop.getTime() > currStart.getTime()) {
            timeframes.push({start: currStart.getTime() > start.getTime() ? currStart : start, stop: stop});
          }
        }
        return timeframes;
      };

      const createTimelines = async (timeframes: ITimeframe[], precedingTimelineItems: (ITimeline | undefined)[]) => {
        const newTimelines = await createNewTimelinesAutoFill(timeframes, precedingTimelineItems);

        const allDraftTimelines = [...(draftTimelines || []), ...(newTimelines || [])];

        const {start} = getCurrentStartDate();
        const allDraftTimelinesSanitized = sanitizeDraftEventList(
          allDraftTimelines,
          start || new Date(),
          isSnapOn,
          !!model.tmsid,
        );

        setDraftTimelinesToModel(allDraftTimelinesSanitized);
        setUndo(allDraftTimelinesSanitized);
      };

      const autofill = async () => {
        const now = new Date();
        setIsAddingQueueAutofill(true);
        handleCloseRightPanel();

        const daysInMiliSec = (daysToAutofillSelected || 7) * 24 * 60 * 60 * 1000;
        // eslint-disable-next-line prefer-const
        let {start, today} = getCurrentStartDate();
        if (model.tmsid && model.gracenoteIntegration?.dynamicClip && draftTimelines) {
          const {maxManualOverride, todayPlus30Min} = findMaxManualOverride(draftTimelines);

          if (maxManualOverride) {
            start = maxManualOverride;
          } else if (start <= today) {
            start = todayPlus30Min;
          }
        } else {
          const safeStart = new Date(now.getTime() + MIN_GAP_TIME * 60 * 1000);
          if (start < safeStart) {
            start = safeStart;
          }
        }
        const stopInMiliSec = start.getTime() + daysInMiliSec;

        let stop = new Date(stopInMiliSec);

        if (endDate && stop > endDate) {
          stop = endDate;
        }

        const timeframes = getTimeframes(start, stop);
        const precedingTimelineItems = timeframes.map(item => getItemAboveTimeFrame(item, sortedDraftTimelines || []));

        await createTimelines(timeframes, precedingTimelineItems);
        // Reset state
        setIsAddingQueueAutofill(false);
      };
      //#endregion AUTOFILL

      const addSeriesToCalendar = (items: IChannelCatalogItemWithState[], doToast = true): boolean => {
        if (!!timeToAddNewEntry && items.length) {
          let nextStartEpisode = timeToAddNewEntry;
          let episodeIndex = (draftTimelines || []).length;

          const newDraftTimelines = items.map(item => {
            const start = normalizeDate(new Date(nextStartEpisode));
            const stop = new Date(nextStartEpisode + item.allotment * 1000);
            const {state, stateMsgList} = getTimelineState({
              ...item,
              start,
              stop,
            });
            item.state = state;
            item.stateMsgList = stateMsgList;
            const timeline: ITimeline = {
              id: `${item.id}-${episodeIndex}`,
              start,
              stop,
              episode: item,
              state,
              stateMsgList,
              isRecurrenceEvent: false,
            };

            nextStartEpisode = nextStartEpisode + item.allotment * 1000;

            episodeIndex = episodeIndex + 1;

            return timeline;
          });

          addEpisodesToCalendar(newDraftTimelines, doToast);
          return true;
        }
        return false;
      };

      const addEpisodesToCalendar = React.useCallback(
        (newDraftTimelines: ITimeline[], doToast = true) => {
          if (!newDraftTimelines.length) return;

          const currentWeekDraftTimelies = draftTimelines?.filter(draft => startDate && draft.stop >= startDate);

          const allDraftTimelines = addEpisodes(newDraftTimelines || [], currentWeekDraftTimelies || [], !!model.tmsid);

          const allDraftTimelinesSanitized = sanitizeDraftEventList(
            allDraftTimelines,
            getCurrentStartDate().start || new Date(),
            isSnapOn,
            !!model.tmsid,
          );

          setDraftTimelinesToModel(allDraftTimelinesSanitized);
          setUndo(allDraftTimelinesSanitized);
          if (doToast) {
            // Count what actually landed (ids are preserved through addEpisodes/sanitize),
            // so the toast can't claim success when nothing was added.
            const addedIds = new Set(newDraftTimelines.map(t => t.id));
            const addedCount = allDraftTimelinesSanitized.filter(t => addedIds.has(t.id)).length;
            if (addedCount > 0) {
              Toast.success(`${addedCount} Episodes were added to the Calendar`);
            } else {
              Toast.warning('No episodes could be added to the Calendar.');
            }
          }
        },
        [draftTimelines, getCurrentStartDate, isSnapOn, setDraftTimelinesToModel, setUndo, startDate, model.tmsid],
      );

      const addTimelinesToCalendar = React.useCallback(
        (items: (ISimpleScheduleEntry | IChannelCatalogItem)[], timeToAddNewEntry?: number) => {
          const listHasTimelines = items.some(isSimpleScheduleEntry);

          const startDateToDrop = listHasTimelines
            ? getStartTimeToDrop(items as ISimpleScheduleEntry[], timeToAddNewEntry)
            : timeToAddNewEntry;

          if (
            (!listHasTimelines && !timeToAddNewEntry) ||
            (!startDateToDrop && !timestampRef.current) ||
            (startDateToDrop &&
              startDate &&
              endDate &&
              (new Date(startDateToDrop) < startDate || new Date(startDateToDrop) > endDate))
          ) {
            Toast.error(`No area defined to paste the ${items.length === 1 ? 'entry' : 'entries'} on the calendar.`);
            return;
          }

          if (startDateToDrop) timestampRef.current = startDateToDrop;

          // Queue → calendar (paste/drag) must stack vertically; ignore positioning tool.
          const placementMode = !listHasTimelines || !timeToAddNewEntry ? 'vertical-select' : copyMode;

          addEpisodesToCalendar(
            recalculateEventTimes(
              items,
              clipboardTimelinesRef.current || [],
              new Date((startDateToDrop || timestampRef.current)!),
              placementMode,
            ),
          );

          setSelectedEpisodes([]);
          setSelectedQueue([]);
          setShouldOpenRightInfoPanel(false);
        },
        [addEpisodesToCalendar, copyMode, endDate, startDate],
      );

      const handleAddRepeatSchedule = async (episodeToRepeat: IChannelCatalogItem | ISimpleScheduleEntry) => {
        const lastRepeatRequest = new Date().getTime();
        setLastRepeatSchedule(lastRepeatRequest);

        const response = await handleRepeatSchedule(episodeToRepeat, lastRepeatRequest);
        setRecurrenceResponse(response);
      };

      const findOverlappingRecurrentEvents = React.useCallback((allEvents: ITimeline[], recurrentEvents: any[]) => {
        const overlappingRecurrentEvents: any[] = [];
        if (recurrentEvents.length === 0) {
          return overlappingRecurrentEvents;
        }

        recurrentEvents.forEach(recurrentEvent => {
          const recurrentEventStart = luxon.fromISO(recurrentEvent.start);
          const recurrentEventEnd = luxon.fromISO(recurrentEvent.stop);
          if (hasOverlap(recurrentEventStart, recurrentEventEnd, allEvents)) {
            overlappingRecurrentEvents.push({
              start: recurrentEventStart.toISO(),
              stop: recurrentEventEnd.toISO(),
            });
          }
        });
        return overlappingRecurrentEvents;
      }, []);

      const mergeWithoutDuplicates = (draftTimelines: ITimeline[], futureEvents: ITimeline[]) => {
        // Filter out duplicates from futureEvents
        const uniqueFutureEvents = futureEvents.filter(item => {
          return !draftTimelines.some(draft => draft.start === item.start && draft.stop === item.stop);
        });
        return [...draftTimelines, ...uniqueFutureEvents];
      };

      const timelineOnDrop = React.useCallback(
        (selected: ISelectedDrop[], timestamp: number) => {
          if (!selected || !selected.length || !timestamp) {
            return;
          }

          if (
            isLockedSlot(timestamp, {
              gracenoteEnabled: model.gracenoteIntegration?.enabled,
              cfaasEnabled: model.cfaasIntegration?.enabled,
            })
          ) {
            Toast.warning('Episode starting within 30 minutes cannot be added.');
            return;
          }

          // check if the operation is about moving timelines around the schedule or dropping new ones from the queue
          const isDropping =
            !draftTimelines || !draftTimelines?.find(dt => selected.some(sel => dt.id === sel.id && sel.start));

          if (isDropping && (model.gracenoteIntegration?.enabled || model.cfaasIntegration?.enabled)) {
            return;
          }

          if (isDropping) {
            trigger('changeTimelineDragHorizontal', false);
            const selection = selectedQueue.length ? selectedQueue : selected;
            const droppedTimelines = selection.map(item =>
              findEpisodeInQueue({...item, episode: item.id}),
            ) as IChannelCatalogItem[];

            const recalculatedTimelines = recalculateEventTimes(
              droppedTimelines,
              clipboardTimelinesRef.current || [],
              new Date(timestamp!),
              'vertical-select',
            ).map(timeline => {
              const fulltimelineData = {...timeline, ...timeline.episode};
              const {state, stateMsgList} = getTimelineState(fulltimelineData);
              return {...timeline, state, stateMsgList};
            });

            addEpisodesToCalendar(recalculatedTimelines);
          } else {
            // It is moving timelines position
            const selection = selected || selectedEpisodes;
            const movedTimelines = (draftTimelines || []).filter(dt =>
              selection.some(sel => dt.id === sel.id && sel.start),
            );

            let nextStartEpisode = timestamp;

            // For group-select mode, maintain relative time differences between episodes
            if (copyMode === 'group-select' && movedTimelines.length > 1) {
              // Sort timelines by their original start time
              const sortedTimelines = [...movedTimelines].sort((a, b) => a.start.getTime() - b.start.getTime());

              // Calculate the time difference from the first episode to the new timestamp
              const firstEpisodeOriginalStart = sortedTimelines[0].start.getTime();
              const timeDifference = timestamp - firstEpisodeOriginalStart;

              const newTimelines = sortedTimelines.map(item => {
                const duration = item.episode.allotment * 1000;
                // Calculate new start time by adding the time difference to the original start time
                const newStartTime = item.start.getTime() + timeDifference;
                const timeline: ITimeline = {
                  ...item,
                  start: normalizeDate(new Date(newStartTime)),
                  stop: new Date(newStartTime + duration),
                };

                const fulltimelineData = {...timeline, ...timeline.episode};
                const {state, stateMsgList} = getTimelineState(fulltimelineData);
                timeline.state = state;
                timeline.stateMsgList = stateMsgList;
                return timeline;
              });

              const existingWithoutMovedTimelines = (draftTimelines || []).filter(
                dt => !selection.some(sel => dt.id === sel.id && sel.start),
              );
              const allDraftTimelines = addEpisodes(newTimelines || [], existingWithoutMovedTimelines, !!model.tmsid);
              const allDraftTimelinesSanitized = sanitizeDraftEventList(
                allDraftTimelines,
                getCurrentStartDate().start || new Date(),
                isSnapOn,
                !!model.tmsid,
              );

              setDraftTimelinesToModel(allDraftTimelinesSanitized);
              setUndo(allDraftTimelinesSanitized);
            } else {
              // For vertical-select and horizontal-select modes, use the original logic
              const newTimelines = movedTimelines.map(item => {
                const duration = item.episode.allotment * 1000;
                const timeline: ITimeline = {
                  ...item,
                  start: normalizeDate(new Date(nextStartEpisode)),
                  stop: new Date(nextStartEpisode + duration),
                };

                if (copyMode !== 'group-select') {
                  nextStartEpisode =
                    copyMode === 'vertical-select'
                      ? nextStartEpisode + duration
                      : addDays(new Date(nextStartEpisode), 1).getTime();
                }

                const fulltimelineData = {...timeline, ...timeline.episode};
                const {state, stateMsgList} = getTimelineState(fulltimelineData);
                timeline.state = state;
                timeline.stateMsgList = stateMsgList;
                return timeline;
              });

              const existingWithoutMovedTimelines = (draftTimelines || []).filter(
                dt => !selection.some(sel => dt.id === sel.id && sel.start),
              );
              const allDraftTimelines = addEpisodes(newTimelines || [], existingWithoutMovedTimelines, !!model.tmsid);
              const allDraftTimelinesSanitized = sanitizeDraftEventList(
                allDraftTimelines,
                getCurrentStartDate().start || new Date(),
                isSnapOn,
                !!model.tmsid,
              );

              setDraftTimelinesToModel(allDraftTimelinesSanitized);
              setUndo(allDraftTimelinesSanitized);
            }
          }
          handleCloseRightPanel();
        },
        [
          addEpisodesToCalendar,
          copyMode,
          draftTimelines,
          findEpisodeInQueue,
          getCurrentStartDate,
          handleCloseRightPanel,
          isSnapOn,
          selectedEpisodes,
          selectedQueue,
          setDraftTimelinesToModel,
          setUndo,
          model.tmsid,
          model.cfaasIntegration?.enabled,
          model.gracenoteIntegration?.enabled,
        ],
      );

      React.useEffect(() => {
        const checkRepeatScheduleResp = async () => {
          if (!recurrenceResponseFromModal || !lastRepeatSchedule) return;

          /**
           * Triggered when the search is successful
           * - check if there are overlapping events with the current timeline events
           * - if there are no overlapping events, add them to the calendar
           * - if there are overlapping events, display an error message
           */
          const {
            data: repeatScheduleItems,
            error: repeatScheduleError,
            originalArgs: repeatScheduleOriginalArgs,
          } = recurrenceResponseFromModal as any;

          // if isRepeatScheduleSuccess failed, run a toast error to inform the user there are no episodes to add
          if (repeatScheduleError) {
            const message =
              get(repeatScheduleError, 'error.data.message', null) ||
              get(repeatScheduleError, 'error.error.data.message', null) ||
              '';
            if ((message as string) === 'Timelines not found by channel, start and params') {
              Toast.error('There are no episodes to add to the Calendar');
            }
            setRecurrenceResponse(undefined);
            setComposedRecurrenceMessage(false);
            return;
          }

          if (repeatScheduleItems?.length && repeatScheduleOriginalArgs?.lastUpdate === lastRepeatSchedule) {
            // create an empty array to store the draft-timelines that are in the future
            const futureEvents: ITimeline[] = [];
            try {
              // fetch the draft-timelines that are in the future to check for overlapping events
              const startScheduleDate = new Date(repeatScheduleItems[0].start);

              // get the latest element of repeatScheduleItems to use it as stop
              const stopScheduleDate = new Date(repeatScheduleItems[repeatScheduleItems.length - 1].stop);

              let startScheduleTime: number | undefined;
              const stopScheduleTime = stopScheduleDate.getTime();

              // if recurrence starts in a future week
              if (endDate && startScheduleDate > endDate) {
                startScheduleTime = startScheduleDate.getTime();

                // if recurrence starts in the current week and ends in a future week
              } else if (endDate && stopScheduleDate > endDate) {
                startScheduleTime = endDate.getTime() + 1;
              }

              if (startScheduleTime && stopScheduleTime) {
                const resp = await summon.get<void, ITimeline[]>(
                  `channels/${channelId}/timeline-drafts?start=${startScheduleTime}&lastUpdate=${lastProgrammingUpdate.valueOf()}&stop=${stopScheduleTime}`,
                );

                if (!resp) return;
                // push the events, but parsing the start and stop for each item as Date
                futureEvents.push(
                  ...resp.map(item => {
                    return {
                      ...item,
                      start: new Date(item.start),
                      stop: new Date(item.stop),
                    };
                  }),
                );
              }
            } catch (e) {}
            // Concatenate draftTimelines with futureEvents
            const allDraftTimelines = mergeWithoutDuplicates(draftTimelines || [], futureEvents);
            const overlapingEvents = findOverlappingRecurrentEvents(allDraftTimelines, repeatScheduleItems);
            if (overlapingEvents.length > 0) {
              if (composedRecurrenceMessage) {
                // warn the user about the overlapping events
                Toast.warning(
                  '1 episode was added to the calendar. Overlapping events were detected and the Recurrence could not be fulfilled as configured.',
                );
              } else {
                Toast.error(
                  `There are ${overlapingEvents.length} events that overlap with the current timeline. Please remove them before adding the new events.`,
                );
              }
              setRecurrenceResponse(undefined);
              setComposedRecurrenceMessage(false);
              return;
            }

            let episodeIndex = (draftTimelines || []).length - 1;
            const newEvents = [
              ...(draftTimelines || []),
              ...(repeatScheduleItems || []).map(item => {
                const episode = findEpisodeInQueue(item);
                const start = new Date(item.start);

                episodeIndex = episodeIndex + 1;
                return {
                  ...item,
                  id: `${item.episode}-${episodeIndex}`,
                  start,
                  stop: new Date(item.stop),
                  locked:
                    start < getMinStart() || model.gracenoteIntegration?.enabled || model.cfaasIntegration?.enabled,
                  episode: episode || item.episode || {},
                  state: episode?.state,
                  stateMsgList: episode?.stateMsgList,
                  isRecurrenceEvent: true,
                  title: episode?.series.name,
                  subTitle: episode?.name,
                };
              }),
            ];

            const {start} = getCurrentStartDate();
            const allDraftTimelinesSanitized = sanitizeDraftEventList(newEvents, start, isSnapOn, !!model.tmsid);
            setDraftTimelinesToModel(allDraftTimelinesSanitized);
            setUndo(allDraftTimelinesSanitized);

            // check if an initial episode was added to the calendar along with the recurrence
            const totalAdded = composedRecurrenceMessage ? repeatScheduleItems.length + 1 : repeatScheduleItems.length;
            Toast.success(`${totalAdded} Episodes were added to the Calendar`);
            setComposedRecurrenceMessage(false);
            setRecurrenceResponse(undefined);
          }
        };

        setTimeout(() => checkRepeatScheduleResp(), 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [recurrenceResponseFromModal, lastRepeatSchedule]);

      const showScreeningDate = React.useMemo(
        () => shouldOpenRightInfoPanel && selectedEpisodes.length === 1,
        [selectedEpisodes.length, shouldOpenRightInfoPanel],
      );

      const selectedItemsForDetails = React.useMemo(() => {
        if (shouldOpenRightInfoPanel) {
          return [...selectedEpisodes, ...selectedQueue];
        } else {
          return [];
        }
      }, [selectedEpisodes, selectedQueue, shouldOpenRightInfoPanel]);

      React.useEffect(() => {
        setIsRightExpanded(Boolean(selectedItemsForDetails.length) && shouldOpenRightInfoPanel);
      }, [selectedItemsForDetails.length, shouldOpenRightInfoPanel]);

      const onEntryClick = React.useCallback(
        (id: string, ev: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
          if (!id) return;

          const cleanup = () => {
            isRightExpanded && setIsRightExpanded(false);
            shouldOpenRightInfoPanel && setShouldOpenRightInfoPanel(false);
          };

          if (ev.detail === 1 && eventInTimeline[id]) {
            setShouldOpenRightInfoPanel(false);
            setEventInTimeline({});
          }

          if (ev.detail === 1 && !eventInTimeline[id]) {
            setEventInTimeline({
              [id]: true,
            });
          }

          // Only by doble click the right info panel will open
          if (ev.detail === 2) {
            setShouldOpenRightInfoPanel(true);
            setEventInTimeline({
              [id]: true,
            });

            return;
          }

          if (!selectedItemsForDetails.length) {
            cleanup();
          }

          if (selectedQueue.length > 0) {
            setSelectedQueue([]);
          }

          const entryIndex = (isDraft ? draftTimelinesEntries || [] : publishedTimelinesEntries).findIndex(
            entry => entry.id === id,
          );
          if (entryIndex !== -1) {
            handleClickItems([entryIndex]);
          }
        },
        [
          draftTimelinesEntries,
          handleClickItems,
          isDraft,
          isRightExpanded,
          publishedTimelinesEntries,
          selectedItemsForDetails,
          selectedQueue,
          shouldOpenRightInfoPanel,
          setIsRightExpanded,
          setShouldOpenRightInfoPanel,
          setSelectedQueue,
          eventInTimeline,
        ],
      );

      const onClickConflictGroup = React.useCallback(
        entries => {
          if (!selectedItemsForDetails.length) {
            setShouldOpenRightInfoPanel(true);
          }

          if (selectedQueue.length > 0) {
            setSelectedQueue([]);
          }

          const indexes: number[] = [];

          entries.forEach(e => {
            const entryIndex = (isDraft ? draftTimelinesEntries || [] : publishedTimelinesEntries).findIndex(
              entry => entry.id === e.id,
            );

            if (entryIndex > -1) {
              indexes.push(entryIndex);
            }
          });

          handleClickItems(indexes);
        },
        [
          draftTimelinesEntries,
          handleClickItems,
          isDraft,
          publishedTimelinesEntries,
          selectedItemsForDetails,
          selectedQueue,
        ],
      );

      const lockedToastShownRef = React.useRef(false);

      React.useEffect(() => {
        const reset = () => {
          lockedToastShownRef.current = false;
        };
        on('startTimelineDrag', reset);
        on('stopTimelineDrag', reset);
        return () => {
          off('startTimelineDrag', reset);
          off('stopTimelineDrag', reset);
        };
      }, []);

      const canDrop = React.useCallback(
        timestamp => {
          if (model.gracenoteIntegration?.enabled || model.cfaasIntegration?.enabled) {
            return false;
          }

          trigger('changeTimelineDragHorizontal', copyMode === 'horizontal-select');

          const locked = isLockedSlot(timestamp, {
            gracenoteEnabled: model.gracenoteIntegration?.enabled,
            cfaasEnabled: model.cfaasIntegration?.enabled,
          });

          if (locked && timestamp > Date.now() && !lockedToastShownRef.current) {
            Toast.warning('Episode starting within 30 minutes cannot be added.');
            lockedToastShownRef.current = true;
          }

          return !locked;
        },
        [copyMode, model.cfaasIntegration?.enabled, model.gracenoteIntegration?.enabled],
      );

      const onAddEntry = React.useCallback((timestamp: number) => {
        // Validate that the timestamp is at least MIN_GAP_TIME minutes in the future
        const currentDate = Date.now();
        const gapMin = (timestamp - currentDate) / 60000;
        if (gapMin < MIN_GAP_TIME) {
          Toast.warning(`Episode starting within ${MIN_GAP_TIME} minutes cannot be added.`);
          return;
        }

        setTimeToAddNewEntry(timestamp);
        setIsAddEpisodeOpen(true);
        setSelectedEpisodes([]);
      }, []);

      const handleDrop = React.useCallback(() => {
        if (
          !ableTo('CHANNEL_EPG_EDIT') ||
          !clipboardRef.current.length ||
          model.gracenoteIntegration?.enabled ||
          model.cfaasIntegration?.enabled
        ) {
          return;
        }

        const timestamp = timelineRef.current?.getTimestamp() || 0;

        if (
          isLockedSlot(timestamp, {
            gracenoteEnabled: model.gracenoteIntegration?.enabled,
            cfaasEnabled: model.cfaasIntegration?.enabled,
          })
        ) {
          Toast.warning('Episode starting within 30 minutes cannot be added.');
          return;
        }

        addTimelinesToCalendar(clipboardRef.current, timestamp);
      }, [ableTo, addTimelinesToCalendar, model.gracenoteIntegration?.enabled, model.cfaasIntegration?.enabled]);

      const preventAddTimeline = React.useMemo(
        () =>
          !isDraft ||
          !ableTo('CHANNEL_EPG_EDIT') ||
          model.gracenoteIntegration?.enabled ||
          model.cfaasIntegration?.enabled,
        [ableTo, isDraft, model],
      );
      const timelineEntries = React.useMemo(
        () => (isDraft ? draftTimelinesEntries || [] : publishedTimelinesEntries || []),
        [draftTimelinesEntries, isDraft, publishedTimelinesEntries],
      );

      const sanitizedSelectedEpisodes = React.useMemo(() => {
        if (selectedEpisodes.length === 0) return selectedEpisodes;
        const validIds = new Set(timelineEntries.map(e => e.id));
        // Keep queue rows (no `start`); only filter stale calendar entries.
        return selectedEpisodes.filter(ep => ep && (!isSimpleScheduleEntry(ep) || validIds.has(ep.id)));
      }, [selectedEpisodes, timelineEntries]);

      const isTimelineLoading = React.useMemo(
        () => isScheduleLoading || !startDate || isDiscardTimelineDraftsLoading,
        [isDiscardTimelineDraftsLoading, isScheduleLoading, startDate],
      );

      const handleBulkRemove = React.useCallback(
        (entries: ISimpleScheduleEntry[]) => {
          const newArray = (draftTimelines || []).filter(item => !entries.find(e => e.id === item.id));

          setDraftTimelinesToModel(newArray);

          setSelectedEpisodes(prevItems => prevItems.filter(item => !entries.find(e => item.id === e.id)));
          setUndo(newArray);
        },
        [draftTimelines, setDraftTimelinesToModel, setUndo],
      );

      const estimateRowHeight = React.useCallback(() => 24.66, []);

      useKeyboardShortcut('ctrl+c, command+c', handleCopy);
      useKeyboardShortcut('ctrl+v, command+v', handleDrop);
      useKeyboardShortcut('ctrl+z, command+z', handleUndo);
      useKeyboardShortcut('shift+ctrl+z, shift+command+z', handleRedo);
      useKeyboardShortcut('delete, backspace', (event: KeyboardEvent) => {
        if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) {
          return;
        }
        handleDelete();
      });

      return (
        <>
          <React.Suspense fallback={<LoadingBox />}>
            <LeaveCalendarDialog
              programHasChanges={programHasChanges}
              isOpenLeaveDialog={isOpenLeaveDialog}
              onClickSaveHandler={onClickSaveHandler}
              attemptToLeave={attemptToLeave}
              cancelAttemptToLeave={cancelAttemptToLeave}
              setIsOpenLeaveDialog={setIsOpenLeaveDialog}
              moveToPeriod={moveToPeriod}
            />
          </React.Suspense>

          <Cluster growNthChild={2} fullHeight wrap={false} id='epg-queue-calendar-cluster'>
            <Expand
              isExpanded={isQueueExpanded && isDraft && ableTo('CHANNEL_EPG_EDIT')}
              fullHeightContainer
              height='100%'
              width='35vw'
            >
              <Template label='expandable'>
                <div ref={queueContainerRef} onClick={() => setIsQueueActive(true)} style={{height: '100%'}}>
                  <Box borderRight={true} borderSize='2px' borderColor='bone' fullHeight={true} background='pewter'>
                    <Box fullHeight paddingY='xxsmall' paddingX='xxsmall'>
                      <Cover scrolling={true} gutterTop='xxsmall' id='epg-queue' coverId='epg-queue-cover'>
                        <Template label='header'>
                          <Stack space='xxsmall'>
                            <Cluster justify='space-between' wrap={false} space='small'>
                              <div style={{width: '20%'}}>
                                <Stack space='xxxsmall'>
                                  <Heading level='h5' renderAs='tiny' color='secondary'>
                                    Queue
                                  </Heading>
                                  <Heading level='h4' truncate={true} truncateBackgroundHover='pewter'>
                                    {model.name}
                                  </Heading>
                                </Stack>
                              </div>
                              <div style={{width: '50%'}}>
                                <Box width='100%'>
                                  <TextInput
                                    id={queueSearchInputId}
                                    value={queueSearch}
                                    onChange={val => {
                                      setQueueSearch(val);
                                      // When cleared via clearable button, val will be empty string
                                      if (!val) {
                                        handleQueueFiltering({name: undefined});
                                      }
                                    }}
                                    placeholder='Search episode name'
                                    clearable={true}
                                    size='small'
                                  />
                                </Box>
                              </div>
                              <div style={{width: '30%'}}>
                                <Stack space='xxxsmall'>
                                  <Paragraph size='small' color='secondary' textAlign='right'>
                                    Total Episodes: {queueTotalItems}
                                  </Paragraph>
                                  <Paragraph size='small' color='secondary' textAlign='right'>
                                    Total Hours: {isQueueLoading ? '' : secondsToHms(queueDuration || -1)}
                                  </Paragraph>
                                </Stack>
                              </div>
                            </Cluster>
                            <Divider color='graphite' />
                          </Stack>
                        </Template>
                        <Template label='cover'>
                          {isQueueExpanded && (
                            <>
                              <Table
                                size='tiny'
                                draggable={true}
                                onSelect={rows => {
                                  trigger('changeTimelineDragHorizontal', false);
                                  setSelectedEpisodes(rows as any);
                                  // Clear the queue multi-select state without broadcasting an empty
                                  // selectedTimelineEntries, which would wipe the drag handler's payload.
                                  setSelectedQueue([]);
                                }}
                                selectable='multiple'
                                onSort={queueOnSort}
                                sortDir={queueSort.split(':')[1] as 'asc' | 'dsc'}
                                sortCol={TableColumnLabel[queueSort.split(':')[0]]}
                                // Union both selection paths (they're mutually exclusive here) so the
                                // row checkboxes mirror the episodes stacked in the right info panel.
                                selected={[...selectedEpisodes, ...selectedQueue] as any}
                                onRowClick={onClickQueueTableRow}
                                wrapContent={true}
                                overflowWrap={true}
                                cols={[
                                  {
                                    label: '',
                                    transform: row => <RowState row={row} />,
                                    colWidth: '1.25rem',
                                    colMinWidth: '1.25rem',
                                    zeroRightPadding: true,
                                  },
                                  {
                                    label: 'Author',
                                    sortable: true,
                                    colWidth: '20%',
                                    transform: (row, col, index) => (
                                      <TdMultilineTruncate
                                        row={row}
                                        onClick={(_r, _c, ev) => onClickQueueTableRow(row, index, ev)}
                                        truncateOnLine={2}
                                        text={row.author || ''}
                                      />
                                    ),
                                  },
                                  {
                                    label: 'Series',
                                    sortable: true,
                                    colWidth: '20%',
                                    transform: (row, col, index) => (
                                      <TdMultilineTruncate
                                        row={row}
                                        onClick={(_r, _c, ev) => onClickQueueTableRow(row, index, ev)}
                                        truncateOnLine={2}
                                        text={row.series?.name || ''}
                                      />
                                    ),
                                  },
                                  {
                                    label: 'Episode',
                                    sortable: true,
                                    colWidth: '20%',
                                    transform: (row, col, index) => (
                                      <TdMultilineTruncate
                                        row={row}
                                        onClick={(_r, _c, ev) => onClickQueueTableRow(row, index, ev)}
                                        truncateOnLine={2}
                                        text={row.name || ''}
                                      />
                                    ),
                                  },
                                  {
                                    label: 'S',
                                    colWidth: '3rem',
                                    sortable: true,
                                    field: 'season',
                                  },
                                  {
                                    label: 'E',
                                    colWidth: '3rem',
                                    sortable: true,
                                    field: 'number',
                                  },
                                  {
                                    label: 'Rat.',
                                    sortable: true,
                                    colWidth: '3rem',
                                    colMinWidth: '3rem',
                                    field: 'rating',
                                  },
                                  {
                                    label: 'Dur.',
                                    sortable: true,
                                    colWidth: '3.7rem',
                                    colMinWidth: '3.7rem',
                                    transform: row => secondsToHms(row.duration!),
                                  },
                                ]}
                                rows={queueItemsRetrieved}
                                loading={isQueueLoading}
                                lazyLoading={isQueueLazyLoading}
                                onLazyLoad={handleQueueLazyLoad}
                                virtual={true}
                                estimateItemHeight={estimateRowHeight}
                                predicate='id'
                                onCustomRowDrag={onCustomRowDrag}
                              ></Table>
                            </>
                          )}
                        </Template>
                      </Cover>
                    </Box>
                  </Box>
                </div>
              </Template>
            </Expand>
            <Box fullHeight background='pewter' overflow='auto' id='epg-box'>
              <Cover scrolling={true} coverId='epg-cover'>
                <Template label='header'>
                  <Box paddingX='small'>
                    <Cluster align='center' justify='space-between' wrap={false}>
                      {canModifyEdit ? (
                        <Box id='showQueue'>
                          <Box hideAt='desktopAndUp'>
                            <Icon
                              space='xxsmall'
                              icon='collapseleft'
                              rotate={isQueueExpanded ? '' : '180deg'}
                              size='large'
                              onClick={() => setIsQueueExpanded(!isQueueExpanded)}
                            />
                          </Box>
                          <Box hideAt='laptopAndDown'>
                            <Icon
                              space='xxsmall'
                              icon='collapseleft'
                              iconAlign='center'
                              color='white'
                              textColor='neutral'
                              rotate={isQueueExpanded ? '' : '180deg'}
                              size='large'
                              onClick={() => setIsQueueExpanded(!isQueueExpanded)}
                            >
                              {isQueueExpanded ? 'Hide' : 'Show'} Queue
                            </Icon>
                          </Box>
                        </Box>
                      ) : (
                        <Box width='10.1875rem'></Box>
                      )}
                      <Cluster
                        space={{laptop: 'xxsmall', desktop: 'medium', wide: 'xlarge'}}
                        align='center'
                        justify='center'
                        wrap={true}
                        id='epg-header'
                      >
                        {!isDraft && (
                          <Button id='printCalendar' icon='print' onClick={() => window.print()}>
                            Print
                          </Button>
                        )}
                        {canModifyEdit && (
                          <Box minWidth='3rem'>
                            <Select
                              id='daysNumber'
                              size='small'
                              value={{label: daysToAutofillSelected.toString()}}
                              options={autofillDaysOptions}
                              onChange={value => setDaysToAutofillSelected(value.value)}
                              state={model.gracenoteIntegration?.enabled ? 'disabled' : ''}
                            />
                          </Box>
                        )}
                        {canModifyEdit && (
                          <Popover
                            appendToBody={true}
                            manualTrigger={true}
                            visible={
                              !existGapsInTimeline &&
                              openMsgWhenAutofillDisabled &&
                              !isDraftTimelinesLoading &&
                              !isQueueLoading
                            }
                          >
                            <Template label='trigger'>
                              <div
                                onMouseOver={() => (!existGapsInTimeline ? setOpenMsgWhenAutofillDisabled(true) : null)}
                                onMouseLeave={() =>
                                  !existGapsInTimeline ? setOpenMsgWhenAutofillDisabled(false) : null
                                }
                              >
                                <Button
                                  id='autoFillButton'
                                  onClick={autofill}
                                  permission={permissions.CHANNEL_EPG_EDIT}
                                  type='primary'
                                  state={
                                    !existGapsInTimeline || isPageLoading || model.gracenoteIntegration?.enabled
                                      ? 'disabled'
                                      : isAddingQueueAutofill
                                      ? 'thinking'
                                      : ''
                                  }
                                >
                                  Auto Fill
                                </Button>
                              </div>
                            </Template>
                            <Template label='popover'>
                              <Box paddingX='small' paddingY='xxxxsmall' background='charcoal'>
                                <Stack>
                                  <Icon space='xxsmall' size='small' icon='info' color='info' iconAlign='center'>
                                    <Help>There are no empty slots in the calendar week to auto fill.</Help>
                                  </Icon>
                                </Stack>
                              </Box>
                            </Template>
                          </Popover>
                        )}
                        {canModifyEdit && (
                          <Box>
                            <Checkbox
                              id='snapOn'
                              label='Snap On'
                              value={isSnapOn}
                              onChange={() => setIsSnapOn(!isSnapOn)}
                              state={isPageLoading ? 'disabled' : ''}
                            />
                          </Box>
                        )}
                        {canModifyEdit && (
                          <Box minWidth='5.5rem'>
                            <Select
                              id='incrementTime'
                              size='small'
                              predicate='value'
                              value={{value: increment, label: ''}}
                              options={[
                                {label: '1 Min.', value: 'small'},
                                {label: '15 Min.', value: 'medium'},
                              ]}
                              onChange={val => setIncrement(val.value)}
                            />
                          </Box>
                        )}
                        {canModifyEdit && (
                          <Box minWidth='5rem'>
                            <Select
                              id='positioning'
                              size='small'
                              predicate='value'
                              value={{value: copyMode, label: ''}}
                              options={[
                                {label: 'Vert.', value: 'vertical-select'},
                                {label: 'Horiz.', value: 'horizontal-select'},
                                {label: 'Group', value: 'group-select'},
                              ]}
                              onChange={val => {
                                trigger('changeTimelineDragHorizontal', val.value === 'horizontal-select');
                                setCopyMode(val.value);
                              }}
                            />
                          </Box>
                        )}
                        <Box minWidth='7rem' maxWidth='9.0rem'>
                          <Cluster space='small' wrap={false} align='center'>
                            <Label>Zoom</Label>
                            <Range id='zoom-slider' min={0} max={2} value={zoomLevel} onChange={debouncedZoom} />
                          </Cluster>
                        </Box>
                        <Box minWidth='4rem'>
                          <Cluster buttonGroup={true} wrap={false}>
                            <Button
                              id='dayCalendar'
                              active={viewType === 'day' ? true : false}
                              onClick={() => switchView('day')}
                            >
                              Day
                            </Button>
                            <Button
                              id='weekCalendar'
                              active={viewType === 'week' ? true : false}
                              onClick={() => switchView('week')}
                            >
                              Week
                            </Button>
                          </Cluster>
                        </Box>
                      </Cluster>
                      {isDraft && (
                        <Button
                          type='secondary'
                          onClick={() => {
                            setIsDraft(false);
                          }}
                          id='viewPublished'
                          permission={permissions.CHANNEL_EPG_VIEW}
                        >
                          View Pub. Calendar
                        </Button>
                      )}
                      {!isDraft && (
                        <Button
                          type='secondary'
                          onClick={() => setIsDraft(true)}
                          id='viewDraft'
                          permission={permissions.CHANNEL_EPG_VIEW}
                        >
                          View Draft Calendar
                        </Button>
                      )}
                    </Cluster>
                  </Box>
                </Template>
                <Template label='cover'>
                  <Timeline
                    id='epg'
                    loading={isTimelineLoading}
                    selected={sanitizedSelectedEpisodes as ISimpleScheduleEntry[]}
                    onPaste={handleDrop}
                    preventAdd={preventAddTimeline}
                    ref={timelineRef}
                    days={viewType === 'day' ? 1 : 7}
                    startDate={dayValue}
                    zoomLevel={zoomLevel}
                    type='schedule'
                    intervals={increment}
                    entries={timelineEntries}
                    entryMinWidth='7rem'
                    canDrop={canDrop}
                    onClearConflictGroup={handleBulkRemove}
                    onClickConflictGroup={onClickConflictGroup}
                    onEntryClick={onEntryClick}
                    onAddEntry={onAddEntry}
                    onDrop={timelineOnDrop}
                    transformEntry={transformEntry}
                    transformTopEntry={transformTopEntry}
                    onHandleWhenClickOutsideAnEntry={handleCloseRightPanel}
                  />
                </Template>
              </Cover>
            </Box>
            <Drawer isOpen={isRightExpanded} onClose={() => handleCloseRightPanel()} width='33rem'>
              <Box paddingY='medium' paddingRight='medium' paddingLeft='medium' fullHeight ref={episodeRecurrenceRef}>
                <Cover scrolling={true} gutterTop='large'>
                  <Template label='header'>
                    <Stack space='large'>
                      <Stack space='xxsmall'>
                        {selectedItemsForDetails.length === 1 && (
                          <Box paddingRight='large'>
                            <Heading level='h3' truncate truncateBackgroundHover='cavern'>
                              {selectedItemsForDetails[0].name}
                            </Heading>
                          </Box>
                        )}
                        {selectedItemsForDetails.length > 1 && (
                          <Heading level='h3' truncate truncateBackgroundHover='cavern'>
                            {`(${selectedItemsForDetails.length}) Episodes Selected`}
                          </Heading>
                        )}
                        {showScreeningDate && (
                          <Paragraph color='secondary' size='small'>
                            {formatScreeningTime(
                              isSimpleScheduleEntry(selectedItemsForDetails[0])
                                ? selectedItemsForDetails[0].start
                                : undefined,
                              selectedItemsForDetails[0].allotment as number,
                            )}
                          </Paragraph>
                        )}
                      </Stack>
                      <Divider color='graphite' />
                      <Cluster buttonGroup={true} justify='center'>
                        <Button
                          active={episodeInfo === 'details' ? true : false}
                          onClick={() => setEpisodeInfo('details')}
                        >
                          Details
                        </Button>
                        {selectedItemsForDetails.length === 1 && (
                          <Button
                            active={episodeInfo === 'recurrence' ? true : false}
                            onClick={() => setEpisodeInfo('recurrence')}
                            permission={permissions.CHANNEL_EPG_EDIT}
                          >
                            Recurrence
                          </Button>
                        )}
                      </Cluster>
                    </Stack>
                  </Template>
                  <Template label='cover'>
                    {episodeInfo === 'details' && selectedItemsForDetails.length > 0 && (
                      <EpisodesDetails
                        items={selectedItemsForDetails}
                        onRemove={isDraft ? handleRemove : undefined}
                        onError={handleCloseRightPanel}
                      />
                    )}
                    {episodeInfo === 'recurrence' && (
                      <Stack space='xlarge'>
                        <Heading level='h3' color='secondary'>
                          Set Recurrence
                        </Heading>
                        <Stack space='medium'>
                          <Heading level='h4'>Repeat</Heading>
                          <Grid minimum='11.25rem' gap='medium'>
                            <FormItem
                              label='Repeat Every'
                              helpText={recurrenceWarning.timeframe}
                              state={recurrenceWarning.timeframe ? 'error' : ''}
                              onFocus={() => {
                                setIsEditingRecurrenceField(true);
                              }}
                              onBlur={() => {
                                setIsEditingRecurrenceField(false);
                              }}
                            >
                              <TextInput
                                type='number'
                                value={repeatPeriod}
                                onChange={value => {
                                  setRepeatPeriod(value);
                                  setIsEditingRecurrenceField(true);
                                }}
                              />
                            </FormItem>
                            <FormItem label='Timeframe'>
                              <Select
                                predicate='value'
                                options={[
                                  {label: 'Day(s)', value: 'day'},
                                  {label: 'Week(s)', value: 'week'},
                                  {label: 'Month(s)', value: 'month'},
                                ]}
                                value={{label: '', value: timeframe}}
                                onChange={value => {
                                  setTimeframe(value.value);
                                }}
                              />
                            </FormItem>
                          </Grid>
                          {timeframe === 'week' && (
                            <Stack space='small'>
                              <Label>Repeat on which day(s) of the week</Label>
                              <Cluster buttonGroup={true}>
                                <Button active={sunRepeat} onClick={() => setSunRepeat(!sunRepeat)}>
                                  Sun
                                </Button>
                                <Button active={monRepeat} onClick={() => setMonRepeat(!monRepeat)}>
                                  Mon
                                </Button>
                                <Button active={tueRepeat} onClick={() => setTueRepeat(!tueRepeat)}>
                                  Tue
                                </Button>
                                <Button active={wedRepeat} onClick={() => setWedRepeat(!wedRepeat)}>
                                  Wed
                                </Button>
                                <Button active={thuRepeat} onClick={() => setThuRepeat(!thuRepeat)}>
                                  Thu
                                </Button>
                                <Button active={friRepeat} onClick={() => setFriRepeat(!friRepeat)}>
                                  Fri
                                </Button>
                                <Button active={satRepeat} onClick={() => setSatRepeat(!satRepeat)}>
                                  Sat
                                </Button>
                              </Cluster>
                              {/** Provides some extra space */}
                              <Box height='0.625rem'></Box>
                            </Stack>
                          )}
                          {timeframe === 'month' && (
                            <FormItem label='Positioning'>
                              <Select
                                predicate='value'
                                options={[
                                  {label: 'Same Day of the Week', value: 'week'},
                                  {label: 'Same Day of the Month', value: 'month'},
                                ]}
                                value={{label: '', value: positioning}}
                                onChange={opt => {
                                  setPositioning(opt.value);
                                }}
                              />
                            </FormItem>
                          )}
                        </Stack>
                        <Stack space='medium'>
                          <Heading level='h4'>End Repeat</Heading>
                          <FormItem label='End Type'>
                            <Select
                              predicate='value'
                              options={[
                                {label: 'End On Date', value: 'date'},
                                {label: 'End On Occurence', value: 'after'},
                              ]}
                              value={{label: '', value: endRepeatType}}
                              onChange={value => {
                                setEndRepeatType(value.value);
                              }}
                            />
                          </FormItem>
                          {endRepeatType === 'date' && (
                            <div
                              onBlur={() => {
                                setIsEditingRecurrenceField(false);
                              }}
                              onInput={() => {
                                setIsEditingRecurrenceField(true);
                              }}
                              onFocus={() => {
                                setIsEditingRecurrenceField(true);
                              }}
                            >
                              <FormItem
                                label='Date'
                                helpText={recurrenceWarning.occurrences}
                                state={recurrenceWarning.occurrences ? 'error' : ''}
                              >
                                <DateTime
                                  id='endRecurrenceDate'
                                  onBeforeDateRender={date => disablePastDates(date, startDate)}
                                  value={endRepeatDate}
                                  onChange={val => {
                                    setEndRepeatDate(val as Date);
                                    setIsEditingRecurrenceField(true);
                                  }}
                                />
                              </FormItem>
                            </div>
                          )}
                          {endRepeatType === 'after' && (
                            <FormItem
                              label='End after how many occurrences'
                              helpText={recurrenceWarning.occurrences}
                              state={recurrenceWarning.occurrences ? 'error' : ''}
                              onFocus={() => {
                                setIsEditingRecurrenceField(true);
                              }}
                              onBlur={() => {
                                setIsEditingRecurrenceField(false);
                              }}
                            >
                              <TextInput type='number' value={occurrences} onChange={value => setOccurrences(value)} />
                            </FormItem>
                          )}
                        </Stack>
                        <Stack space='medium'>
                          <Heading level='h4'>Cycle</Heading>
                          <FormItem label='Cycle Over'>
                            <Select
                              predicate='value'
                              options={[
                                {label: 'Episodes and Seasons in the Series', value: 'series'},
                                {label: 'Episode in this Season', value: 'season'},
                              ]}
                              value={{label: '', value: cycleOver}}
                              onChange={value => {
                                setCycleOver(value.value);
                              }}
                            />
                          </FormItem>
                        </Stack>
                        <Cluster justify='space-between'>
                          <div></div>
                          <Button
                            type='primary'
                            state={isValidRecurrence ? '' : 'disabled'}
                            onClick={() =>
                              handleAddRepeatSchedule(selectedEpisodes.length ? selectedEpisodes[0] : selectedQueue[0])
                            }
                          >
                            Apply
                          </Button>
                        </Cluster>
                      </Stack>
                    )}
                  </Template>
                </Cover>
              </Box>
            </Drawer>
          </Cluster>
          {isAddEpisodeOpen && (
            <React.Suspense fallback={<React.Fragment />}>
              <EpisodeQueueSearchModal
                isAddEpisodeOpen={isAddEpisodeOpen}
                setIsAddEpisodeOpen={setIsAddEpisodeOpen}
                isRecurrenceOpen={isRecurrenceOpen}
                setIsRecurrenceOpen={setIsRecurrenceOpen}
                addEpisodesToCalendar={addSeriesToCalendar}
                setComposedRecurrenceMessage={setComposedRecurrenceMessage}
                channelId={channelId}
                startDate={startDate}
                setRecurrenceResponse={setRecurrenceResponse}
                timeToAddNewEntry={timeToAddNewEntry}
                setLastRepeatSchedule={setLastRepeatSchedule}
              />
            </React.Suspense>
          )}
          {isRecurrenceOpen && selectedEpisodes.length === 1 && (
            <EpisodeRecurrence
              channelId={channelId}
              startDate={startDate}
              searchSelected={selectedEpisodes[0]}
              setIsRecurrenceOpen={setIsRecurrenceOpen}
              isRecurrenceOpen={isRecurrenceOpen}
              setRecurrenceResponse={setRecurrenceResponse}
              setLastRepeatSchedule={setLastRepeatSchedule}
            />
          )}
        </>
      );
    },
  ),
);

const EpisodeTemplate = ({item}: {item: IScheduleEntry}): JSX.Element => (
  <div className='template-test'>
    <Stack space='xxxxsmall'>
      <div className='print-margin'>
        <Heading level='h6' renderAs='tiny' color='secondary'>
          {item.author}
        </Heading>
      </div>
      <div className='print-margin'>
        <Heading level='h6' renderAs='tiny' color='secondary'>
          (S{item.season}: E{item.number}) {item.rating}
        </Heading>
      </div>
    </Stack>
  </div>
);

const EpisodeTopTemplate = ({item}: {item: IScheduleEntry}) => {
  const start = new Date(item.start);
  const stopMs = start.getTime() + item.duration * 1000;
  const stop = new Date(stopMs);

  return (
    <Heading level='h6' renderAs='tiny' color='secondary'>
      {`${formatDateWithLocalTimezone(start, 'h:mm a')} - ${formatDateWithLocalTimezone(stop, 'h:mm a')}`}
    </Heading>
  );
};

export default ProgramPage;
